import { Loader2 } from 'lucide-react';

type TenantHostStatusPageProps = {
  hostname: string;
  status: 'loading' | 'not_found' | 'draft';
};

/**
 * Controlled pre-login surface for tenant hostnames. Used while tenant branding is
 * being resolved (loading), for unknown tenant hostnames (Tenant Not Found), and
 * when the tenant's branding is withheld because the site is an unpublished draft.
 * Never renders the Trade-Proof marketing site or any base brand assets.
 */
export default function TenantHostStatusPage({ hostname, status }: TenantHostStatusPageProps) {
  const isNotFound = status === 'not_found';
  const isDraft = status === 'draft';
  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-6 py-12 dark:bg-[#0b0f14]" data-testid="tenant-host-status-page">
      <div className="w-full max-w-md rounded-2xl border border-zinc-200 p-8 text-center dark:border-slate-800">
        {isNotFound ? (
          <>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-500">Tenant Not Found</p>
            <h1 className="mt-2 text-xl font-semibold text-zinc-900 dark:text-white">This tenant is not available.</h1>
            <p className="mt-2 break-all font-mono text-sm text-zinc-500" data-testid="tenant-host-hostname">
              {hostname || '—'}
            </p>
            <p className="mt-3 text-xs leading-5 text-zinc-400">
              The website for this address could not be found. If you believe this is a mistake, contact the provider.
            </p>
          </>
        ) : isDraft ? (
          <>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-500">Not Published</p>
            <h1 className="mt-2 text-xl font-semibold text-zinc-900 dark:text-white">This website is not available yet.</h1>
            <p className="mt-2 break-all font-mono text-sm text-zinc-500" data-testid="tenant-host-hostname">
              {hostname || '—'}
            </p>
            <p className="mt-3 text-xs leading-5 text-zinc-400">
              The provider hasn't published this tenant website yet. Check back later or contact the provider.
            </p>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-500" />
            <p className="mt-3 text-sm text-zinc-500">Loading tenant…</p>
          </>
        )}
      </div>
    </div>
  );
}
